import React from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Download, Eye, Calendar } from "lucide-react";
import { styles as baseStyles } from "../../style/DoctorProfile.styles";

/**
 * ResearchGrid
 *
 * Props:
 * - doctorId: string
 * - uploads: Array<{
 *     id: string,
 *     title: string,
 *     description?: string,
 *     files?: string[],               // first file can be image or pdf url
 *     downloads?: number,
 *     createdAt?: Date
 *   }>
 * - onOpen?: (upload) => void         // optional override; if not provided, navigates to /research/:doctorId/:uploadId
 * - styles?: typeof baseStyles
 */
const ResearchGrid = ({ doctorId, uploads = [], onOpen, styles = baseStyles }) => {
  const navigate = useNavigate();

  const openItem = (u) => {
    if (onOpen) return onOpen(u);
    if (!doctorId || !u?.id) return;
    navigate(`/research/${doctorId}/${u.id}`);
  };

  const isPdf = (url) => typeof url === "string" && url.toLowerCase().includes(".pdf");

  const formatDate = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric" }).format(d);
    };

  return (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <FileText style={{ width: 20, height: 20, color: "#6366f1" }} />
        <h2 style={styles.sectionTitle}>Research &amp; Publications</h2>
      </div>

      <div style={styles.sectionContent}>
        {uploads.length > 0 ? (
          <div style={styles.researchGrid} className="research-grid">
            {uploads.map((u) => {
              const thumb = u.files?.[0];
              return (
                <div key={u.id} style={styles.researchCard} className="research-card">
                  <div style={styles.researchImage}>
                    {thumb ? (
                      isPdf(thumb) ? (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: "#ef4444",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                          }}
                        >
                          <FileText style={{ width: 48, height: 48 }} />
                        </div>
                      ) : (
                        <img
                          src={thumb}
                          alt={u.title}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      )
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#9ca3af",
                        }}
                      >
                        <FileText style={{ width: 48, height: 48 }} />
                      </div>
                    )}

                    <div
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                        color: "white",
                        padding: "4px 8px",
                        borderRadius: 4,
                        fontSize: 12,
                      }}
                    >
                      {u.files?.length || 0} file{(u.files?.length || 0) !== 1 ? "s" : ""}
                    </div>
                  </div>

                  <h3 style={styles.researchTitle}>{u.title}</h3>
                  {u.description && (
                    <p style={styles.researchDescription}>
                      {u.description.length > 120 ? `${u.description.substring(0, 120)}...` : u.description}
                    </p>
                  )}

                  <div style={styles.researchMeta}>
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <Calendar style={{ width: 12, height: 12 }} />
                      {u.createdAt ? formatDate(u.createdAt) : "—"}
                    </span>
                    <div style={styles.downloadBadge}>
                      <Download style={{ width: 12, height: 12 }} />
                      {u.downloads || 0} downloads
                    </div>
                  </div>

                  <button
                    style={{ ...styles.uploadButton, width: "100%", marginTop: 12, justifyContent: "center" }}
                    onClick={() => openItem(u)}
                  >
                    <Eye style={{ width: 14, height: 14 }} />
                    View Research
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={styles.emptyState}>
            <FileText style={{ width: 48, height: 48, margin: "0 auto 16px auto" }} />
            <p>No research publications available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResearchGrid;
